import React, { useState, useEffect } from 'react';
import AppShell from '../components/AppShell';
import { useAuth } from '../context/AuthContext';
import { Icon } from '../utils/icons';
import axios from 'axios';

const API = process.env.REACT_APP_API_URL || 'http://localhost:3001';

const EVENT_TYPES = {
  showing:              { label: 'Home Showing',              color: '#3a6ad9', text: '#fff',    iconName: 'home' },
  contract:             { label: 'Contract Executed',         color: '#3a6ad9', text: '#fff',    iconName: 'document' },
  offer_accepted:       { label: 'Offer Accepted',            color: '#acf44e', text: '#1a1a1a', iconName: 'check-circle' },
  option_period_end:    { label: 'Option Period Deadline',    color: '#ff082f', text: '#fff',    iconName: 'calendar' },
  emd_due:              { label: 'Down Payment Due',         color: '#ff082f', text: '#fff',    iconName: 'currency' },
  emd_paid:             { label: 'EMD Paid',                  color: '#acf44e', text: '#1a1a1a', iconName: 'check-circle' },
  inspection_appraisal: { label: 'Appraisal Package Ordered', color: '#ffdd86', text: '#1a1a1a', iconName: 'search' },
  inspection_complete:  { label: 'Inspection Completed',           color: '#acf44e', text: '#1a1a1a', iconName: 'check-circle' },
  appraisal:            { label: 'Appraisal Received',             color: '#acf44e', text: '#1a1a1a', iconName: 'check-circle' },
  le_issued:            { label: 'Loan Estimate Issued',      color: '#3a6ad9', text: '#fff',    iconName: 'document' },
  le_received:          { label: 'LE Accepted',               color: '#acf44e', text: '#1a1a1a', iconName: 'check-circle' },
  cd_issued:            { label: 'Closing Disclosure Issued', color: '#3a6ad9', text: '#fff',    iconName: 'document' },
  cd_received:          { label: 'CD Accepted',               color: '#acf44e', text: '#1a1a1a', iconName: 'check-circle' },
  hoi_received:         { label: 'HOI Bound & Uploaded',      color: '#ffdd86', text: '#1a1a1a', iconName: 'lock' },
  title_ordered:        { label: 'Title Ordered',             color: '#ffdd86', text: '#1a1a1a', iconName: 'document' },
  title_received:       { label: 'Title Received',            color: '#acf44e', text: '#1a1a1a', iconName: 'check-circle' },
  // rate_lock_start + rate_lock removed — Doorly's product workflow
  // doesn't bind rate-lock events. The date_rate_lock_* columns stay
  // on the schema for backward compat (zero consumers means zero
  // breakage) but the calendar no longer renders them.
  walkthrough:          { label: 'Final Walkthrough',         color: '#3a6ad9', text: '#fff',    iconName: 'home' },
  closing:              { label: 'Close Date',                color: '#acf44e', text: '#1a1a1a', iconName: 'key' },
  // consummation removed — date_consummation and date_closing are the
  // same moment for Doorly's product; rendering both produced a
  // duplicate event on the calendar. Close Date is the borrower-
  // facing label; date_consummation stays on the DB for TRID/audit
  // consumers but isn't surfaced to the borrower calendar.
  // Inspectify-sourced (option_periods.{inspection,appraisal}_scheduled_at).
  // Time-of-day exists on the source TIMESTAMPTZ but the calendar pins to the
  // date portion; the time can be surfaced in a future detail popover.
  inspection_scheduled: { label: 'Home Inspection',           color: '#3a6ad9', text: '#fff',    iconName: 'search' },
  appraisal_scheduled:  { label: 'Appraisal',                 color: '#acf44e', text: '#1a1a1a', iconName: 'home' },
};

const DAYS = ['Sun','Mon','Tue','Wed','Thu','Fri','Sat'];
const MONTHS = ['January','February','March','April','May','June','July','August','September','October','November','December'];

export default function Schedule() {
  const { token } = useAuth();
  const [events, setEvents] = useState([]);
  const [loading, setLoading] = useState(true);
  // Tracked separately from events so the legend text under the
  // empty-state placeholder can omit "Option period deadline" for
  // new-construction borrowers (the OP doesn't apply to NC).
  const [isNewConstruction, setIsNewConstruction] = useState(false);
  const now = new Date();
  const [year, setYear] = useState(now.getFullYear());
  const [month, setMonth] = useState(now.getMonth());
  const [selectedDay, setSelectedDay] = useState(null);
  const [view, setView] = useState('month');

  useEffect(() => {
    if (!token) return;
    setLoading(true);
    Promise.all([
      axios.get(`${API}/api/users/me/schedule-dates`, { headers: { Authorization: `Bearer ${token}` } }).catch(() => ({ data: {} })),
      axios.get(`${API}/api/homes/my-showings`, { headers: { Authorization: `Bearer ${token}` } }).catch(() => ({ data: { showings: [] } })),
    ]).then(([datesRes, showingsRes]) => {
      const all = [];
      const d = datesRes.data || {};
      setIsNewConstruction(!!d.is_new_construction);
      // Derive combined inspection & appraisal deadline from contract_date + 3 days
      if (!d.date_inspection_appraisal_deadline && d.date_contract) {
        const cd = new Date(String(d.date_contract).slice(0, 10) + 'T12:00:00');
        cd.setDate(cd.getDate() + 3);
        d.date_inspection_appraisal_deadline = cd.toISOString().split('T')[0];
      }
      const dateMap = [
        ['date_contract', 'contract'],
        ['date_offer_accepted', 'offer_accepted'],
        ['date_option_period_end', 'option_period_end'],
        ['date_emd_due', 'emd_due'],
        ['date_emd_paid', 'emd_paid'],
        ['date_inspection_appraisal_deadline', 'inspection_appraisal'],
        ['date_inspection_completed', 'inspection_complete'],
        ['date_appraisal_received', 'appraisal'],
        ['date_le_issued', 'le_issued'],
        ['date_le_received', 'le_received'],
        ['date_cd_issued', 'cd_issued'],
        ['date_cd_received', 'cd_received'],
        ['date_hoi_received', 'hoi_received'],
        ['date_title_ordered', 'title_ordered'],
        ['date_title_received', 'title_received'],
        // date_rate_lock_start / date_rate_lock_expiration intentionally
        // dropped — see EVENT_TYPES comment above.
        ['date_final_walkthrough', 'walkthrough'],
        ['date_closing', 'closing'],
        // date_consummation intentionally not mapped — see EVENT_TYPES
        // comment above.
        // Inspectify-sourced from /me/schedule-dates → option_periods.
        ['inspection_scheduled_at', 'inspection_scheduled'],
        ['appraisal_scheduled_at',  'appraisal_scheduled'],
      ];
      dateMap.forEach(([field, type]) => {
        if (d[field]) {
          // CST-safe: extract YYYY-MM-DD from ISO string or date string
          const raw = String(d[field]);
          const dateStr = raw.length >= 10 ? raw.slice(0, 10) : raw;
          all.push({ id: field, type, date: dateStr, title: EVENT_TYPES[type]?.label || type });
        }
      });
      (showingsRes.data?.showings || []).forEach(s => {
        const dt = s.preferred_date || s.created_at;
        // showing_requests.status is mixed-case ('Pending' | 'Accepted'
        // | 'Confirmed' | 'Completed' | 'Canceled') — the previous
        // lowercase comparison never matched, so showings silently
        // dropped off the calendar. Normalize and include the active
        // trio; Canceled and Completed stay off (Canceled = noise,
        // Completed = past showings the borrower already attended).
        const normalized = String(s.status || '').toLowerCase();
        if (!dt) return;
        if (!['pending', 'accepted', 'confirmed', 'requested'].includes(normalized)) return;
        const isConfirmed = normalized === 'confirmed';
        const isAccepted  = normalized === 'accepted';
        const prefix = isConfirmed ? 'Showing — ' : isAccepted ? 'Accepted — ' : 'Pending — ';
        const fallback = isConfirmed ? 'Home Showing' : isAccepted ? 'Accepted Showing' : 'Pending Showing';
        all.push({
          id: 'sh_' + s.id,
          type: 'showing',
          date: String(dt).slice(0, 10),
          title: s.address ? prefix + s.address.split(',')[0] : fallback,
          time: s.preferred_time,
          status: s.status,
          confirmed: isConfirmed,
          address: s.address,
          notes: s.admin_notes,
        });
      });
      setEvents(all);
    }).finally(() => setLoading(false));
  }, [token]);

  // CST-safe today string using local date
  const todayStr = `${now.getFullYear()}-${String(now.getMonth() + 1).padStart(2, '0')}-${String(now.getDate()).padStart(2, '0')}`;
  const firstDay = new Date(year, month, 1).getDay();
  const daysInMonth = new Date(year, month + 1, 0).getDate();
  const prevDays = new Date(year, month, 0).getDate();
  const totalCells = Math.ceil((firstDay + daysInMonth) / 7) * 7;

  const cells = Array.from({ length: totalCells }, (_, i) => {
    if (i < firstDay) return { day: prevDays - firstDay + i + 1, cur: false };
    const d = i - firstDay + 1;
    if (d > daysInMonth) return { day: d - daysInMonth, cur: false };
    const ds = `${year}-${String(month + 1).padStart(2, '0')}-${String(d).padStart(2, '0')}`;
    return { day: d, cur: true, ds };
  });

  const eventsOn = (ds) => events.filter(e => e.date === ds);
  const upcoming = events.filter(e => e.date >= todayStr).sort((a, b) => a.date.localeCompare(b.date));
  // CST-safe: parse YYYY-MM-DD as local date to avoid timezone shifting
  const daysUntil = (ds) => {
    const [y, m, d] = ds.split('-').map(Number);
    const target = new Date(y, m - 1, d);
    const today = new Date(); today.setHours(0, 0, 0, 0);
    return Math.ceil((target - today) / 86400000);
  };
  const fmtDate = (ds) => {
    const [y, m, d] = ds.split('-').map(Number);
    return new Date(y, m - 1, d).toLocaleDateString('en-US', { weekday: 'long', month: 'long', day: 'numeric' });
  };

  return (
    <AppShell>
      <div style={{ maxWidth: '860px', margin: '0 auto' }}>
        <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: '20px', flexWrap: 'wrap', gap: '10px' }}>
          <div>
            <h1 className="text-2xl font-bold text-black">Schedule</h1>
            <p className="text-gray-500 mt-1 text-sm">Your key dates, showings, and milestones</p>
          </div>
          <div style={{ display: 'flex', gap: '6px' }}>
            <button onClick={() => { setYear(now.getFullYear()); setMonth(now.getMonth()); setSelectedDay(todayStr); }}
              style={{ padding: '6px 12px', background: '#f3f4f6', border: '1px solid #e5e7eb', borderRadius: '8px', cursor: 'pointer', fontSize: '12px', fontWeight: '600', color: '#374151' }}>Today</button>
            {['month', 'list'].map(v => (
              <button key={v} onClick={() => setView(v)}
                style={{ padding: '6px 12px', background: view === v ? '#3a6ad9' : '#f3f4f6', border: 'none', borderRadius: '8px', cursor: 'pointer', fontSize: '12px', fontWeight: '600', color: view === v ? '#fff' : '#374151', textTransform: 'capitalize' }}>
                {v === 'month' ? 'Month' : 'List'}
              </button>
            ))}
          </div>
        </div>

        {loading ? (
          <div style={{ textAlign: 'center', padding: '60px', color: '#9ca3af' }}>Loading...</div>
        ) : (
        <>
        {view === 'month' && (
        <div style={{ background: '#fff', border: '1px solid #e5e7eb', borderRadius: '14px', overflow: 'hidden' }}>
          <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', padding: '14px 18px', borderBottom: '1px solid #f3f4f6' }}>
            <button onClick={() => month === 0 ? (setYear(y => y - 1), setMonth(11)) : setMonth(m => m - 1)}
              style={{ width: '30px', height: '30px', borderRadius: '8px', border: '1px solid #e5e7eb', background: '#fff', cursor: 'pointer', fontSize: '15px', display: 'flex', alignItems: 'center', justifyContent: 'center' }}>‹</button>
            <span style={{ fontSize: '15px', fontWeight: '700', color: '#111827' }}>{MONTHS[month]} {year}</span>
            <button onClick={() => month === 11 ? (setYear(y => y + 1), setMonth(0)) : setMonth(m => m + 1)}
              style={{ width: '30px', height: '30px', borderRadius: '8px', border: '1px solid #e5e7eb', background: '#fff', cursor: 'pointer', fontSize: '15px', display: 'flex', alignItems: 'center', justifyContent: 'center' }}>›</button>
          </div>
          <div style={{ display: 'grid', gridTemplateColumns: 'repeat(7, 1fr)', borderBottom: '1px solid #f3f4f6' }}>
            {DAYS.map(d => <div key={d} style={{ padding: '8px 0', textAlign: 'center', fontSize: '11px', fontWeight: '700', color: '#9ca3af', textTransform: 'uppercase' }}>{d}</div>)}
          </div>
          <div style={{ display: 'grid', gridTemplateColumns: 'repeat(7, 1fr)' }}>
            {cells.map((c, i) => {
              const ce = c.ds ? eventsOn(c.ds) : [];
              const isToday = c.ds === todayStr;
              const isSel = c.ds === selectedDay;
              return (
                <div key={i} onClick={() => c.cur && c.ds && setSelectedDay(isSel ? null : c.ds)}
                  style={{ minHeight: '72px', padding: '4px', borderRight: i % 7 < 6 ? '1px solid #f3f4f6' : 'none', borderBottom: '1px solid #f3f4f6', background: isSel ? '#eff6ff' : isToday ? '#f0fdf4' : '#fff', cursor: c.cur ? 'pointer' : 'default' }}>
                  <div style={{ width: '24px', height: '24px', borderRadius: '50%', display: 'flex', alignItems: 'center', justifyContent: 'center', fontSize: '12px', fontWeight: isToday ? '800' : '500', background: isToday ? '#acf44e' : 'transparent', color: !c.cur ? '#d1d5db' : '#374151', marginBottom: '3px' }}>{c.day}</div>
                  {ce.slice(0, 2).map(e => {
                    const baseCfg = EVENT_TYPES[e.type] || { color: '#6b7280', text: '#fff', iconName: 'bell' };
                    const cfg = e.type === 'showing' && !e.confirmed ? { ...baseCfg, color: '#9ca3af' } : baseCfg;
                    return <div key={e.id} style={{ background: cfg.color, color: cfg.text, borderRadius: '3px', padding: '1px 4px', fontSize: '9px', fontWeight: '600', marginBottom: '1px', overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap', display: 'flex', alignItems: 'center', gap: '2px' }}><Icon name={cfg.iconName} size={8} color={cfg.text} /> {e.title}</div>;
                  })}
                  {ce.length > 2 && <div style={{ fontSize: '9px', color: '#6b7280' }}>+{ce.length - 2}</div>}
                </div>
              );
            })}
          </div>
        </div>
        )}

        {view === 'list' && (
        <div style={{ display: 'flex', flexDirection: 'column', gap: '8px' }}>
          {upcoming.length === 0 ? (
            <div style={{ background: '#fff', border: '1px solid #e5e7eb', borderRadius: '14px', padding: '48px', textAlign: 'center', color: '#9ca3af' }}>
              <div style={{ marginBottom: '10px' }}><Icon name="calendar" size={32} color="#d1d5db" /></div>
              <div style={{ fontWeight: '600', marginBottom: '4px' }}>No upcoming dates</div>
              <div style={{ fontSize: '13px' }}>Key dates will appear here as your loan progresses</div>
            </div>
          ) : upcoming.map(e => {
            const cfg = EVENT_TYPES[e.type] || { color: '#6b7280', text: '#fff', iconName: 'bell', label: e.type };
            const du = daysUntil(e.date);
            return (
              <div key={e.id} style={{ background: '#fff', border: `1px solid ${du <= 3 ? '#fecdd3' : '#e5e7eb'}`, borderLeft: `4px solid ${e.type === 'showing' && !e.confirmed ? '#9ca3af' : cfg.color}`, borderRadius: '10px', padding: '12px 16px', display: 'flex', alignItems: 'center', gap: '14px' }}>
                <div style={{ width: '40px', height: '40px', borderRadius: '8px', background: e.type === 'showing' && !e.confirmed ? '#9ca3af' : cfg.color, display: 'flex', alignItems: 'center', justifyContent: 'center', flexShrink: 0 }}><Icon name={cfg.iconName} size={20} color={cfg.text} /></div>
                <div style={{ flex: 1 }}>
                  <div style={{ fontSize: '14px', fontWeight: '700', color: '#111827' }}>{e.title}</div>
                  <div style={{ fontSize: '12px', color: '#6b7280' }}>
                    {fmtDate(e.date)}{e.time ? ` · ${e.time}` : ''}
                    {e.type === 'showing' && (
                      <span style={{ marginLeft: '8px', fontSize: '10px', fontWeight: '700', padding: '1px 6px', borderRadius: '4px', background: e.confirmed ? '#acf44e' : '#fff7ed', color: e.confirmed ? '#1a1a1a' : '#7a5200' }}>
                        {e.confirmed ? 'Confirmed' : 'Pending'}
                      </span>
                    )}
                  </div>
                  {e.address && <div style={{ fontSize: '11px', color: '#9ca3af', marginTop: '2px' }}>{e.address}</div>}
                </div>
                <span style={{ fontSize: '12px', fontWeight: '700', padding: '3px 10px', borderRadius: '10px', background: du === 0 ? '#acf44e' : du <= 3 ? '#fff0f3' : '#f3f4f6', color: du === 0 ? '#1a1a1a' : du <= 3 ? '#ff082f' : '#374151' }}>
                  {du === 0 ? 'Today' : du === 1 ? 'Tomorrow' : `${du}d`}
                </span>
              </div>
            );
          })}
        </div>
        )}

        {selectedDay && view === 'month' && (
        <div style={{ marginTop: '14px', background: '#fff', border: '1px solid #e5e7eb', borderRadius: '12px', padding: '16px 20px' }}>
          <div style={{ display: 'flex', justifyContent: 'space-between', marginBottom: '12px' }}>
            <span style={{ fontSize: '14px', fontWeight: '700', color: '#111827' }}>{fmtDate(selectedDay)}</span>
            <button onClick={() => setSelectedDay(null)} style={{ background: 'none', border: 'none', cursor: 'pointer', color: '#9ca3af', fontSize: '16px' }}>x</button>
          </div>
          {eventsOn(selectedDay).length === 0 ? (
            <div style={{ textAlign: 'center', padding: '16px', color: '#9ca3af', fontSize: '13px' }}>No events</div>
          ) : eventsOn(selectedDay).map(e => {
            const cfg = EVENT_TYPES[e.type] || { color: '#6b7280', iconName: 'bell' };
            return (
              <div key={e.id} style={{ display: 'flex', alignItems: 'flex-start', gap: '10px', padding: '10px 12px', borderRadius: '8px', background: cfg.color + '15', border: `1px solid ${cfg.color}40`, marginBottom: '6px' }}>
                <span style={{ marginTop: '2px' }}><Icon name={cfg.iconName} size={20} color={cfg.color} /></span>
                <div style={{ flex: 1 }}>
                  <div style={{ fontSize: '14px', fontWeight: '700', color: '#111827' }}>{e.title}</div>
                  {e.type === 'showing' && (
                    <div style={{ fontSize: '11px', color: '#6b7280', marginTop: '4px', lineHeight: '1.6' }}>
                      {e.confirmed
                        ? <span style={{ color: '#2d6a0a', fontWeight: '600' }}>Confirmed</span>
                        : <span style={{ color: '#7a5200', fontWeight: '600' }}>Pending confirmation</span>}
                      {e.time && <span style={{ marginLeft: '8px' }}>· {e.time}</span>}
                      {e.address && <div style={{ marginTop: '2px' }}>{e.address}</div>}
                      {e.notes && <div style={{ marginTop: '4px', fontStyle: 'italic' }}>{e.notes}</div>}
                    </div>
                  )}
                  {e.type !== 'showing' && e.time && <div style={{ fontSize: '12px', color: '#6b7280' }}>{e.time}</div>}
                  {e.type !== 'showing' && e.status && <span style={{ fontSize: '10px', fontWeight: '700', padding: '1px 6px', borderRadius: '4px', background: e.status === 'confirmed' ? '#acf44e' : '#fff7ed', color: e.status === 'confirmed' ? '#1a1a1a' : '#7a5200' }}>{e.status}</span>}
                </div>
              </div>
            );
          })}
        </div>
        )}

        {events.length === 0 && !loading && (
        <div style={{ marginTop: '20px', background: '#f9fafb', border: '1px solid #e5e7eb', borderRadius: '12px', padding: '24px', textAlign: 'center' }}>
          <div style={{ fontSize: '13px', color: '#6b7280', lineHeight: '1.8' }}>
            <strong>Your schedule will show:</strong><br />
            {/* Legend mirrors EVENT_TYPES + dateMap above. Two
                conditionals:
                  · 'Option period deadline' dropped for NC borrowers
                    (the OP doesn't apply to NC transactions).
                  · 'Rate lock' removed entirely — Doorly's product
                    workflow doesn't bind rate-lock events. */}
            Home showings
            {!isNewConstruction && <> &middot; Option period deadline</>}
            {' '}&middot; Down payment due &middot; Loan Estimate &middot; Closing Disclosure &middot; Appraisal Package &middot; HOI &amp; Title &middot; Final walkthrough &middot; Close date
            <br /><br />
            Dates populate automatically as your loan progresses.
          </div>
        </div>
        )}
        </>
        )}
      </div>
    </AppShell>
  );
}
